// ScrivenerWorkbench — per-agent wrapper over <CompositionWorkbench> for
// SCRIVENER, the suitability-letter composer. Injects SCRIVENER's
// invariant block (summary, drift detail, recommendations, risk warnings,
// fee disclosure) — the fields held constant across all three tonal
// variants per ADR-0002.

import CompositionWorkbench from '.';
import { compositionAgentConfig } from './composition-agent-config';
import type { BaseDraft } from '@/data/types';

// SCRIVENER's draft adds the letter-body invariants over the base shape.
export interface ScrivenerDraft extends BaseDraft {
    summary?: string;
    driftDetails?: string;
    recommendations?: string[];
    riskWarnings?: string[];
    feeDisclosure?: string;
}

interface ScrivenerWorkbenchProps {
    draft: ScrivenerDraft;
    readOnly?: boolean;
    onApproved?: (draft: ScrivenerDraft) => void;
    onRejected?: (draft: ScrivenerDraft) => void;
    onRevisionRequested?: (draft: ScrivenerDraft) => void;
}

function InvariantList({ label, items }: { label: string; items: string[] }) {
    if (items.length === 0) return null;
    return (
        <div>
            <div className="text-card-title text-[rgb(var(--color-text-primary))] mb-1">
                {label}
            </div>
            <ul className="list-disc ml-5 flex flex-col gap-0.5">
                {items.map((item, i) => (
                    <li key={i} className="text-annotation text-[rgb(var(--color-text-secondary))]">
                        {item}
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function ScrivenerWorkbench({
    draft,
    readOnly,
    onApproved,
    onRejected,
    onRevisionRequested,
}: ScrivenerWorkbenchProps) {
    return (
        <CompositionWorkbench<ScrivenerDraft>
            agentName="SCRIVENER"
            draft={draft}
            table={compositionAgentConfig.SCRIVENER.table}
            actions={compositionAgentConfig.SCRIVENER.actions}
            readOnly={readOnly}
            renderInvariants={(d) => (
                <div
                    data-testid="scrivener-invariants"
                    className="rounded-md border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface-secondary)/0.4)] p-3 flex flex-col gap-2.5"
                >
                    <div className="text-card-title text-[rgb(var(--color-text-primary))]">
                        Letter invariants (held across variants)
                    </div>
                    {d.summary && (
                        <p className="text-annotation text-[rgb(var(--color-text-primary))]">
                            {d.summary}
                        </p>
                    )}
                    {d.driftDetails && (
                        <p className="text-annotation text-[rgb(var(--color-text-secondary))]">
                            {d.driftDetails}
                        </p>
                    )}
                    <InvariantList label="Recommendations" items={d.recommendations ?? []} />
                    <InvariantList label="Risk warnings" items={d.riskWarnings ?? []} />
                    {d.feeDisclosure && (
                        <div>
                            <div className="text-card-title text-[rgb(var(--color-text-primary))] mb-1">
                                Fee disclosure
                            </div>
                            <p className="text-annotation text-[rgb(var(--color-text-secondary))]">
                                {d.feeDisclosure}
                            </p>
                        </div>
                    )}
                </div>
            )}
            onApproved={onApproved}
            onRejected={onRejected}
            onRevisionRequested={onRevisionRequested}
        />
    );
}
